/**
 * Count-in metronome scheduler. Plays N bars of quarter-note clicks
 * at the current BPM before recording actually starts, so the user
 * has a reliable downbeat to play against.
 *
 * Clicks are scheduled synchronously via Web Audio — every sample
 * lands on an exact context time, regardless of JS main-thread
 * jitter. The returned promise resolves at the downbeat AFTER the
 * last click, so callers can kick off the real recording with no
 * intermediate setTimeout drift.
 *
 * 4/4 is hard-coded — matching the sequencer's own 16-steps-per-bar
 * grid. If we ever add time-signature support, swap `BEATS_PER_BAR`
 * for a project-scoped value.
 *
 * Click tones: a short square-wave beep at 880 Hz on beat 1 of each
 * bar (accent) and 440 Hz on beats 2-4. Mirrors the familiar DAW /
 * metronome convention without pulling in a sample file.
 */

const BEATS_PER_BAR = 4
/** Safety lead-in so clicks don't fire in the same audio block the
 *  scheduler is currently rendering — gives Web Audio a chance to
 *  align the first click on a clean buffer boundary. */
const LEAD_IN_SEC = 0.05

/** Total seconds the caller should wait before starting the real
 *  record. Useful when the UI wants to show a "counting in…" label
 *  with a correct ETA before awaiting the promise. */
export function countInDurationSec(bpm: number, bars: number): number {
  if (bars <= 0 || bpm <= 0) return 0
  const secPerBeat = 60 / bpm
  return bars * BEATS_PER_BAR * secPerBeat
}

/**
 * Play a count-in and resolve at the downbeat immediately after.
 * The caller awaits this before calling their own start path —
 * the promise resolves slightly ahead of `ctx.currentTime` matching
 * the downbeat so there's no JS-thread drift in the handoff.
 *
 * `bars` of `0` is a no-op that resolves immediately.
 */
export async function playCountIn(
  ctx: AudioContext,
  bpm: number,
  bars: number,
): Promise<void> {
  if (bars <= 0) return
  const secPerBeat = 60 / bpm
  const startTime = ctx.currentTime + LEAD_IN_SEC
  const totalBeats = bars * BEATS_PER_BAR
  for (let i = 0; i < totalBeats; i++) {
    const when = startTime + i * secPerBeat
    const isDownbeat = i % BEATS_PER_BAR === 0
    scheduleClick(ctx, when, isDownbeat)
  }
  const endTime = startTime + totalBeats * secPerBeat
  const waitMs = Math.max(0, (endTime - ctx.currentTime) * 1000)
  await new Promise<void>(resolve => {
    setTimeout(resolve, waitMs)
  })
}

/** Schedule one click beep at `when`. The oscillator is one-shot —
 *  started + stopped at absolute times so garbage collection happens
 *  on its own schedule. Envelope is a tiny attack + exponential
 *  decay, audible but short enough that clicks don't bleed into
 *  each other at fast tempos. */
function scheduleClick(
  ctx: AudioContext,
  when: number,
  downbeat: boolean,
): void {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'square'
  osc.frequency.value = downbeat ? 880 : 440
  // 1 ms attack, ~80 ms decay. `exponentialRampToValueAtTime` needs
  // a non-zero target so we land at 0.0001 and then silence after.
  gain.gain.setValueAtTime(0, when)
  gain.gain.linearRampToValueAtTime(downbeat ? 0.35 : 0.22, when + 0.001)
  gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.08)
  osc.connect(gain).connect(ctx.destination)
  osc.start(when)
  osc.stop(when + 0.1)
}
